import * as PIXI from 'pixi.js';
import CanvasTools from '~/view/CanvasTools';
import Tooltip from '~/components/tooltips/Tooltip';
import text from '~/text';
import GameItemIcon from './GameItemIcon';

var EquipmentFigure = function (equipment) {
  try {
    PIXI.Container.call(this);

    var _icons = [];
    this.getIcons = function () {
      return _icons;
    };

    // Helper function to create dummy item objects for rendering
    const createDummyItem = (slotType, itemType = null) => {
      const dummyItems = {
        helmet: { slot: 'helmet', type: 'med', quality: 0 },
        torsoe: { slot: 'torsoe', type: 'med', quality: 0 },
        boots: { slot: 'boots', type: 'med', quality: 0 },
        weapon: { slot: 'weapon', type: 'sword', quality: 0 },
        ring: { slot: 'ring', type: 'ring', quality: 0 },
      };

      if (slotType === 'off_hand') {
        return { slot: 'off_hand', type: itemType, quality: 0 };
      }

      return dummyItems[slotType] || { slot: slotType, type: 'med', quality: 0 };
    };

    // Helper function to create a background icon for empty slots
    const createSlotBackground = (slotType, x, y) => {
      const background = new PIXI.Container();
      background.x = x;
      background.y = y;

      // Create a semi-transparent background
      const blackwash = new PIXI.Graphics();
      blackwash.beginFill(0x000000, 0.3);
      blackwash.drawRect(-26, -26, 52, 52);
      blackwash.endFill();
      background.addChild(blackwash);

      // Add the inventory grid frame
      const frame = new PIXI.Sprite(PIXI.utils.TextureCache['armory/inventory_grid.png']);
      frame.width = frame.height = 52;
      frame.anchor.x = frame.anchor.y = 0.5;
      frame.alpha = 0.5;
      background.addChild(frame);

      // Add greyed-out dummy item GameItemIcon(s)
      if (slotType === 'off_hand') {
        // For off_hand, render 4 smaller items in quadrants
        const offHandTypes = [
          { type: 'bow', pos: { x: -11, y: -11 }, scale: 22 },
          { type: 'trinket', pos: { x: 11, y: -11 }, scale: 35 },
          { type: 'book', pos: { x: -11, y: 11 }, scale: 26 },
          { type: 'shield', pos: { x: 11, y: 11 }, scale: 25 },
        ];

        offHandTypes.forEach(({ type, pos, scale }) => {
          const dummyItem = createDummyItem(slotType, type);
          const itemIcon = new GameItemIcon(dummyItem, {
            frame: false,
            showBlackwash: false,
          });
          itemIcon.setScale(scale);
          itemIcon.x = pos.x;
          itemIcon.y = pos.y;
          itemIcon.img.tint = 0x555555;
          itemIcon.img.alpha = 0.4;
          itemIcon.interactive = itemIcon.buttonMode = false;
          background.addChild(itemIcon);
        });
      } else {
        // For other slots, render a single greyed-out item
        const dummyItem = createDummyItem(slotType);
        const itemIcon = new GameItemIcon(dummyItem, {
          frame: false,
          showBlackwash: false,
        });
        itemIcon.setScale(44);
        itemIcon.img.tint = 0x555555;
        itemIcon.img.alpha = 0.4;
        itemIcon.interactive = itemIcon.buttonMode = false;
        background.addChild(itemIcon);
      }

      // Make it interactive for tooltip
      background.interactive = true;
      background.hitArea = new PIXI.Rectangle(-26, -26, 52, 52);

      let _tooltip = null;
      background.mouseover = background.pointerover = () => {
        if (!_tooltip) {
          const tooltipData = {
            title_text: text(`ui.equipment_slots.${slotType}`),
          };

          // Add valid item types for weapon and off_hand slots
          if (slotType === 'weapon' || slotType === 'off_hand') {
            tooltipData.body_text = text(`ui.equipment_slot_types.${slotType}`);
          }

          _tooltip = new Tooltip('ui', tooltipData, background);
        }
      };
      background.mouseout = background.pointerout = () => {
        if (_tooltip) {
          _tooltip.dispose();
          _tooltip = null;
        }
      };

      // Add methods for compatibility with GameItemIcon interface
      background.getItem = () => null;

      background.dispose = () => {
        if (_tooltip) {
          _tooltip.dispose();
          _tooltip = null;
        }
        background.destroy({ children: true });
      };

      return background;
    };

    const makeHelmetIcon = () => {
      if (!equipment.helmet) {
        const bg = createSlotBackground('helmet', 0, -63);
        bg.slot = 'helmet';
        this.addChild(bg);
        _icons.push(bg);
      } else {
        var helmetIcon = new GameItemIcon(equipment.helmet);
        helmetIcon.slot = 'helmet';
        helmetIcon.x = 0;
        helmetIcon.y = -63;
        this.addChild(helmetIcon);
        _icons.push(helmetIcon);
      }
    };

    const makeTorsoeIcon = () => {
      if (!equipment.torsoe) {
        const bg = createSlotBackground('torsoe', 0, 5);
        bg.slot = 'torsoe';
        this.addChild(bg);
        _icons.push(bg);
      } else {
        var torsoeIcon = new GameItemIcon(equipment.torsoe);
        torsoeIcon.slot = 'torsoe';
        torsoeIcon.x = 0;
        torsoeIcon.y = 5;
        this.addChild(torsoeIcon);
        _icons.push(torsoeIcon);
      }
    };

    const makeLeftHandIcon = () => {
      if (!equipment.off_hand) {
        const bg = createSlotBackground('off_hand', 70, -30);
        bg.slot = 'off_hand';
        this.addChild(bg);
        _icons.push(bg);
      } else {
        var leftHandIcon = new GameItemIcon(equipment.off_hand);
        leftHandIcon.slot = 'off_hand';
        leftHandIcon.x = 70;
        leftHandIcon.y = -30;
        this.addChild(leftHandIcon);
        _icons.push(leftHandIcon);
      }
    };

    const makeRightHandIcon = () => {
      if (!equipment.weapon) {
        const bg = createSlotBackground('weapon', -70, -30);
        bg.slot = 'weapon';
        this.addChild(bg);
        _icons.push(bg);
      } else {
        var rightHandIcon = new GameItemIcon(equipment.weapon);
        rightHandIcon.slot = 'weapon';
        rightHandIcon.x = -70;
        rightHandIcon.y = -30;
        this.addChild(rightHandIcon);
        _icons.push(rightHandIcon);
      }
    };

    let leftRingIcon;
    const makeLeftRingIcon = () => {
      if (!equipment.left_ring) {
        const bg = createSlotBackground('ring', 70, 40);
        bg.slot = 'ring';
        bg.slot_hand = 'left_ring';
        this.addChild(bg);
        _icons.push(bg);
        leftRingIcon = bg;
      } else {
        leftRingIcon = new GameItemIcon(equipment.left_ring);
        leftRingIcon.slot = 'ring';
        leftRingIcon.slot_hand = 'left_ring';
        leftRingIcon.x = 70;
        leftRingIcon.y = 40;
        this.addChild(leftRingIcon);
        _icons.push(leftRingIcon);
      }
    };

    let rightRingIcon;
    const makeRightRingIcon = () => {
      if (!equipment.right_ring) {
        const bg = createSlotBackground('ring', -70, 40);
        bg.slot = 'ring';
        bg.slot_hand = 'right_ring';
        this.addChild(bg);
        _icons.push(bg);
        rightRingIcon = bg;
      } else {
        rightRingIcon = new GameItemIcon(equipment.right_ring);
        rightRingIcon.slot = 'ring';
        rightRingIcon.slot_hand = 'right_ring';
        rightRingIcon.x = -70;
        rightRingIcon.y = 40;
        this.addChild(rightRingIcon);
        _icons.push(rightRingIcon);
      }
    };

    const makeBootsIcon = () => {
      if (!equipment.boots) {
        const bg = createSlotBackground('boots', 0, 71);
        bg.slot = 'boots';
        this.addChild(bg);
        _icons.push(bg);
      } else {
        var bootsIcon = new GameItemIcon(equipment.boots);
        bootsIcon.slot = 'boots';
        bootsIcon.x = 0;
        bootsIcon.y = 71;
        this.addChild(bootsIcon);
        _icons.push(bootsIcon);
      }
    };

    this.init = () => {
      makeHelmetIcon();
      makeTorsoeIcon();
      makeLeftHandIcon();
      makeRightHandIcon();
      makeLeftRingIcon();
      makeRightRingIcon();
      makeBootsIcon();

      for (const icon of _icons) {
        // Only call setScale on GameItemIcon instances, not on background containers
        if (icon.setScale) {
          icon.setScale(56);
        }
      }
    };

    this.renderSpinner = (item) => {
      const icon = _icons.find((i) => i && i.getItem() && i.getItem().uid === item.uid);
      if (icon) {
        CanvasTools.makeFaerieSpinners(icon, 7);
      }
    };

    this.renderSpinnerForItemToEquip = (item) => {
      const icon =
        item.slot === 'ring'
          ? equipment.leftRing
            ? rightRingIcon
            : leftRingIcon
          : _icons.find((i) => i.slot === item.slot);

      if (icon) {
        CanvasTools.makeFaerieSpinners(icon, 7);
      }
    };

    this.dispose = () => {
      if (_icons) {
        for (const icon of _icons) {
          if (icon) {
            icon.dispose();
          }
        }
        _icons = null;
      }

      this.destroy();
    };
  } catch (err) {
    logError(
      err,
      Object.assign(
        {
          module: 'components/common/EquipmentFigure',
          func: 'constructor',
        },
        equipment
      )
    );
  }
};
EquipmentFigure.prototype = Object.create(PIXI.Container.prototype);
EquipmentFigure.prototype.constructor = EquipmentFigure;
export default EquipmentFigure;
